// Copyright (c) Bentley Systems
import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox } from "@itwin/itwinui-react";
import { ExternalLink } from "./Models/external-link";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setConfirmation } from "../app/confirmation-slice";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import {
  checkInternalServerError,
  getUrlBasedOnEnv,
} from "../Helpers/helper-functions";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { selectAccessToken } from "../app/authorization-slice";
import { CONTENT_TYPES, HTTP_HEADERS } from "../Helpers/http-constants";

/**
 * Props for the ExternalLinkForm component.
 */
export type ExternalLinkFormProps = {
  /** The external link to edit. If undefined, the form is in "create" mode. */
  externalLink?: ExternalLink;
  /** The index for a new external link. Used only when creating a new item. */
  index?: number;
  /** The iTwin ID associated with this external link. Required for API calls. */
  iTwinId: string;
  /** The iModel ID associated with this external link. Required for API calls. */
  iModelId: string;
  /** Callback function to close the form and optionally refresh the parent list. */
  closeFrollFunction?: (
    refreshList: boolean,
    externalLink?: ExternalLink,
  ) => void;
  /** Optional callback for navigation to a specific panel URL. */
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
};

/**
 * Form component for creating or editing external links.
 * Handles validation, submission, and displays confirmation dialogs for unsaved changes.
 *
 * @param props - The component props of type ExternalLinkFormProps
 * @returns A form for managing external link data
 */
const ExternalLinkForm = (props: ExternalLinkFormProps) => {
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();

  const [caption, setCaption] = useState<string>();
  const [url, setUrl] = useState<string>();
  const [showInExternalViewsDialog, setShowInExternalViewsDialog] =
    useState<boolean>(true);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<{
    [key: string]: "positive" | "warning" | undefined;
  }>({});

  const handleStatusChange = (
    id: string,
    status: "positive" | "warning" | undefined,
  ) => {
    setStatuses((prevStatuses) => {
      if (prevStatuses[id] !== status) {
        return {
          ...prevStatuses,
          [id]: status,
        };
      }
      return prevStatuses;
    });
  };

  const allInputStatusesValid = Object.values(statuses).every(
    (status) => status !== "warning",
  );

  useEffect(() => {
    if (caption && url) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [caption, url]);

  const wasFormChanged = useMemo(() => {
    if (props.externalLink !== undefined) {
      // If externalLink exists but has no id, it's from URL parameters (new item)
      if (!props.externalLink.id) {
        return true; // Always consider it changed for new items from URL params
      }

      if (
        props.externalLink.caption === caption &&
        props.externalLink.url === url &&
        props.externalLink.showInExternalViewsDialog ===
          showInExternalViewsDialog &&
        props.externalLink.enabled === enabled
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (caption || url || !showInExternalViewsDialog || !enabled) {
        return true;
      } else {
        return false;
      }
    }
  }, [caption, url, showInExternalViewsDialog, enabled, props.externalLink]);

  useEffect(() => {
    setIsFormDataChanged(wasFormChanged);
  }, [wasFormChanged]);

  useEffect(() => {
    if (props.externalLink !== undefined) {
      setCaption(props.externalLink.caption);
      setUrl(props.externalLink.url);
      setShowInExternalViewsDialog(
        props.externalLink.showInExternalViewsDialog,
      );
      setEnabled(props.externalLink.enabled);
    }
  }, [props.externalLink, dispatch]);

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (
    refreshList: boolean,
    externalLink: ExternalLink | undefined = undefined,
  ) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList, externalLink);
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      const propsRunId = crypto.randomUUID();
      const isEdit = props.externalLink !== undefined;
      const externalLinkId = props.externalLink?.id;

      const body = {
        index: props.externalLink?.index ?? props.index,
        caption: caption as string,
        url: url as string,
        showInExternalViewsDialog,
        enabled,
      };

      const method = isEdit && externalLinkId ? "PUT" : "POST";
      const apiUrl =
        isEdit && externalLinkId
          ? getUrlBasedOnEnv(
              `/api/ExternalLinks/itwins/${props.iTwinId}/imodels/${props.iModelId}/externallinks/${externalLinkId}`,
            )
          : getUrlBasedOnEnv(
              `/api/ExternalLinks/itwins/${props.iTwinId}/imodels/${props.iModelId}`,
            );

      const response = await fetch(apiUrl, {
        method,
        headers: {
          [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
          [HTTP_HEADERS.AUTHORIZATION]: accessToken,
          [HTTP_HEADERS.RUN_ID]: propsRunId,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const responseData = await response.json();
        checkInternalServerError(
          response.status,
          dispatch,
          responseData.runId || propsRunId,
        );
        showToast({
          status: "negative",
          message: `Failed to ${isEdit ? "update" : "create"} external link: ${response.status}`,
        });
        setIsSaving(false);
        return;
      }

      const savedItem = await response.json();
      const savedExternalLink = savedItem.externalLink as ExternalLink;

      showToast({
        status: "positive",
        message: `External link ${isEdit && externalLinkId ? "updated" : "created"} successfully!`,
      });

      // Small delay to let user see the success message before closing
      setTimeout(() => {
        closeFRoll(true, savedExternalLink);
      }, 500);
    } catch {
      showToast({
        status: "negative",
        message: "An error occurred while saving the external link.",
      });
      setIsSaving(false);
    }
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      <div style={{ height: "100%" }}>
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="becs-infopanelbody-footer-line"
              style={{
                flex: 1,
                overflowY: "auto",
              }}
            >
              <div className="becs-all-margin">
                <LabeledInputComponent
                  required={true}
                  validationType={EfFieldValidationTypeEnum.Default}
                  onStatusChange={(status) =>
                    handleStatusChange("caption", status)
                  }
                  displayStyle="default"
                  label="Caption"
                  placeholder="Enter text here..."
                  maxLength={100}
                  key="LbCaption"
                  defaultValue={caption}
                  onChange={(currentValue) => {
                    setCaption(currentValue.target.value);
                  }}
                />

                <div className="becs-t-margin">
                  <LabeledInputComponent
                    required={true}
                    validationType={EfFieldValidationTypeEnum.Url}
                    onStatusChange={(status) =>
                      handleStatusChange("url", status)
                    }
                    displayStyle="default"
                    label="Url"
                    placeholder="https://example.com"
                    maxLength={250}
                    key="LbUrl"
                    defaultValue={url}
                    onChange={(currentValue) => {
                      setUrl(currentValue.target.value);
                    }}
                  />
                </div>

                <div className="becs-t-margin">
                  <Checkbox
                    label={"Enabled"}
                    key={"ChbEnabled"}
                    onChange={(e) => {
                      setEnabled(e.target.checked);
                    }}
                    checked={enabled}
                  />
                </div>
                <div className="becs-t-margin">
                  <Checkbox
                    label={"Show in external views dialog"}
                    key={"ChbShowInExternalViewsDialog"}
                    onChange={(e) => {
                      setShowInExternalViewsDialog(e.target.checked);
                    }}
                    checked={showInExternalViewsDialog}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div className="becs-all-margin">
                <div className="rowWrap">
                  <Button
                    key="saveCredentialBtn"
                    as="button"
                    styleType="cta"
                    type="submit"
                    disabled={
                      !(
                        isFormValid &&
                        isFormDataChanged &&
                        allInputStatusesValid
                      ) || isSaving
                    }
                  >
                    <div>
                      {isSaving
                        ? "Saving..."
                        : props.externalLink === undefined ||
                            !props.externalLink.id
                          ? "Save"
                          : "Update"}
                    </div>
                  </Button>
                  <Button
                    key="cancelCredentialBtn"
                    as="button"
                    onClick={() => onCancelClick()}
                  >
                    <div>Cancel</div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExternalLinkForm;
